import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { Card, Btn, PageHeader, InfoAlert } from '../components/UI';
import { IconCheck, IconRefresh } from '../components/Icons';

// ── Field definitions — label + description for each setting key ──────────────
const FIELD_META = {
  // Branding
  site_name:             { label: 'Site Name',              description: 'Displayed in the browser tab, top bar, and emails.' },
  site_tagline:          { label: 'Tagline',                description: 'Short subtitle shown under the site name.' },
  logo_url:              { label: 'Logo URL',               description: 'Hosted image URL for your logo. Leave blank to use the default WiFi icon.' },

  // Support
  support_email:         { label: 'Support Email',          description: 'Users will see this as the contact address for help.' },
  support_phone:         { label: 'Support Phone',          description: 'Optional phone number shown on the portal.' },
  support_whatsapp:      { label: 'WhatsApp Number',        description: 'Include country code, e.g. +2348012345678.' },

  // Payments
  currency_code:         { label: 'Currency Code',          description: 'ISO code used in API responses, e.g. NGN, USD.' },
  currency_symbol:       { label: 'Currency Symbol',        description: 'Symbol rendered next to amounts, e.g. ₦ or $.' },
  min_topup_amount:      { label: 'Minimum Top-up (₦)',     description: 'Smallest amount a user can add to their wallet.' },

  // Operations
  default_device_limit:  { label: 'Default Device Limit',   description: 'How many devices a plan allows before an add-on is required.' },
  expiry_grace_mins:     { label: 'Expiry Grace (minutes)', description: 'Extra minutes of access after a subscription expires before revoking.' },
  expiry_warning_hours:  { label: 'Expiry Warning (hours)', description: 'Hours before expiry to send a warning email to the user.' },

  // Legal
  terms_url:             { label: 'Terms of Service URL',   description: 'Link shown in the portal footer. Leave blank to hide.' },
  privacy_url:           { label: 'Privacy Policy URL',     description: 'Link shown in the portal footer. Leave blank to hide.' },

  // Appearance
  theme:                 { label: 'Portal Theme',           description: 'Visual style of the user-facing portal. Applies instantly on their next page load.' },

  // Maintenance
  maintenance_mode:      { label: 'Maintenance Mode',       description: 'Set to true to show a maintenance message to all users.' },
  maintenance_message:   { label: 'Maintenance Message',    description: 'Message shown to users when maintenance mode is on.' },
};

const GROUPS = [
  { group: 'Branding',    keys: ['site_name', 'site_tagline', 'logo_url'] },
  { group: 'Appearance',  keys: ['theme'] },
  { group: 'Support',     keys: ['support_email', 'support_phone', 'support_whatsapp'] },
  { group: 'Payments',    keys: ['currency_code', 'currency_symbol', 'min_topup_amount'] },
  { group: 'Operations',  keys: ['default_device_limit', 'expiry_grace_mins', 'expiry_warning_hours'] },
  { group: 'Legal',       keys: ['terms_url', 'privacy_url'] },
  { group: 'Maintenance', keys: ['maintenance_mode', 'maintenance_message'] },
];

export default function Config() {
  const [settings, setSettings] = useState({});
  const [edits, setEdits]       = useState({});
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const toast = useToast();

  const load = useCallback(() => {
    setLoading(true);
    api.getSiteSettings()
      .then(res => setSettings(res.settings || {}))
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!Object.keys(edits).length) return toast('No changes to save.', 'warning');
    setSaving(true);
    try {
      await api.updateSiteSettings(edits);
      toast('Settings saved!', 'success');
      setEdits({});
      load();
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const dirtyCount = Object.keys(edits).length;

  // Build grouped items from live settings
  const grouped = GROUPS.map(({ group, keys }) => ({
    group,
    items: keys
      .filter(k => settings[k] !== undefined && settings[k] !== null)
      .map(k => ({ key: k, value: String(settings[k]), ...FIELD_META[k] })),
  })).filter(g => g.items.length > 0);

  return (
    <div className="page-enter">
      <PageHeader
        title="Site Settings"
        subtitle="Manage branding, support contacts, payments, and portal behaviour"
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn size="sm" variant="ghost" icon={IconRefresh}
              onClick={() => { setEdits({}); load(); }}>
              Reset
            </Btn>
            <Btn size="sm" icon={IconCheck} onClick={save} loading={saving} disabled={!dirtyCount}>
              Save{dirtyCount > 0 ? ` (${dirtyCount} change${dirtyCount > 1 ? 's' : ''})` : ' Changes'}
            </Btn>
          </div>
        }
      />

      {dirtyCount > 0 && (
        <InfoAlert type="warning" style={{ marginBottom: 20 }}>
          ⚠ You have <b>{dirtyCount}</b> unsaved change{dirtyCount > 1 ? 's' : ''}. Click <b>Save Changes</b> to apply.
        </InfoAlert>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 80, color: 'var(--text3)' }}>
          <div style={{ width: 24, height: 24, border: '2px solid var(--border2)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.6s linear infinite', margin: '0 auto 12px' }} />
          Loading settings…
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {grouped.map(({ group, items }) => (
            <Card key={group}>
              <h3 style={{
                fontSize: 11, fontWeight: 700, color: 'var(--text3)',
                textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 18,
              }}>
                {group}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
                {items.map(item => {
                  const isDirty   = edits[item.key] !== undefined;
                  const currentVal = isDirty ? edits[item.key] : item.value;
                  const isBoolean = item.key === 'maintenance_mode';
                  const isTheme   = item.key === 'theme';
                  const isLong    = item.key === 'maintenance_message';

                  return (
                    <div key={item.key} style={{
                      background: isDirty ? 'rgba(34,197,94,0.06)' : 'var(--surface2)',
                      borderRadius: 10, padding: 14,
                      border: `1px solid ${isDirty ? 'rgba(34,197,94,0.3)' : 'var(--border)'}`,
                      transition: 'all 0.15s',
                      gridColumn: isLong ? 'span 2' : undefined,
                    }}>
                      {/* Key name */}
                      <div style={{
                        fontSize: 11, fontWeight: 700,
                        color: isDirty ? 'var(--green)' : 'var(--text3)',
                        marginBottom: 3, fontFamily: 'Geist Mono, monospace',
                      }}>
                        {item.key}
                      </div>

                      {/* Human label */}
                      {item.label && (
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                          {item.label}
                        </div>
                      )}

                      {/* Description */}
                      {item.description && (
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, lineHeight: 1.5 }}>
                          {item.description}
                        </div>
                      )}

                      {/* Input */}
                      {isTheme ? (
                        <select
                          value={currentVal}
                          onChange={e => setEdits(prev => ({ ...prev, [item.key]: e.target.value }))}
                          style={{
                            width: '100%', padding: '8px 10px', borderRadius: 6,
                            border: `1px solid ${isDirty ? 'rgba(34,197,94,0.4)' : 'var(--border2)'}`,
                            background: 'var(--surface)', color: 'var(--text)',
                            fontSize: 13, fontFamily: 'inherit', outline: 'none', cursor: 'pointer',
                          }}
                        >
                          <option value="dark-neon">Dark neon — modern telecom</option>
                          <option value="light-fintech">Light — fintech / banking</option>
                          <option value="bold-campus">Bold — high-contrast campus</option>
                        </select>
                      ) : isBoolean ? (
                        <select
                          value={currentVal}
                          onChange={e => setEdits(prev => ({ ...prev, [item.key]: e.target.value }))}
                          style={{
                            width: '100%', padding: '8px 10px', borderRadius: 6,
                            border: `1px solid ${isDirty ? 'rgba(34,197,94,0.4)' : 'var(--border2)'}`,
                            background: 'var(--surface)', color: 'var(--text)',
                            fontSize: 13, fontFamily: 'inherit', outline: 'none', cursor: 'pointer',
                          }}
                        >
                          <option value="false">false — Portal is live</option>
                          <option value="true">true — Maintenance mode ON</option>
                        </select>
                      ) : isLong ? (
                        <textarea
                          value={currentVal}
                          onChange={e => setEdits(prev => ({ ...prev, [item.key]: e.target.value }))}
                          rows={3}
                          style={{
                            width: '100%', padding: '8px 10px', borderRadius: 6,
                            border: `1px solid ${isDirty ? 'rgba(34,197,94,0.4)' : 'var(--border2)'}`,
                            background: 'var(--surface)', color: 'var(--text)',
                            fontSize: 13, fontFamily: 'inherit', outline: 'none',
                            resize: 'vertical', boxSizing: 'border-box',
                          }}
                        />
                      ) : (
                        <input
                          value={currentVal}
                          onChange={e => setEdits(prev => ({ ...prev, [item.key]: e.target.value }))}
                          style={{
                            width: '100%', padding: '8px 10px', borderRadius: 6,
                            border: `1px solid ${isDirty ? 'rgba(34,197,94,0.4)' : 'var(--border2)'}`,
                            background: 'var(--surface)', color: 'var(--text)',
                            fontSize: 13, fontFamily: 'Geist Mono, monospace', outline: 'none',
                            boxSizing: 'border-box',
                          }}
                        />
                      )}

                      {/* Revert button */}
                      {isDirty && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                          <button
                            onClick={() => setEdits(prev => { const n = { ...prev }; delete n[item.key]; return n; })}
                            style={{ fontSize: 10, color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                            ↩ Revert
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}